import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import { Search, Rocket, Target, Briefcase, FolderKanban, Building2, Users2, Globe } from 'lucide-react';
export function SearchTab({ missions, goals, portfolios, projects, organizations, teams, workspaces, onSelectMission, onSelectGoal, onSelectPortfolio, onSelectProject }) {
    const [searchQuery, setSearchQuery] = useState('');
    // Combine all items into searchable results
    const allResults = useMemo(() => {
        const query = searchQuery.toLowerCase();
        if (!query)
            return [];
        const results = [];
        // Search missions
        missions.forEach(mission => {
            if (mission.name.toLowerCase().includes(query) || mission.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'mission',
                    id: mission.id,
                    title: mission.name,
                    description: mission.description,
                    status: mission.status,
                    icon: Rocket,
                    onClick: () => onSelectMission?.(mission)
                });
            }
        });
        // Search goals
        goals.forEach(goal => {
            if (goal.title.toLowerCase().includes(query) || goal.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'goal',
                    id: goal.id,
                    title: goal.title,
                    description: goal.description,
                    status: goal.status,
                    icon: Target,
                    onClick: () => onSelectGoal?.(goal)
                });
            }
        });
        // Search portfolios
        portfolios.forEach(portfolio => {
            if (portfolio.name.toLowerCase().includes(query) || portfolio.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'portfolio',
                    id: portfolio.id,
                    title: portfolio.name,
                    description: portfolio.description,
                    status: portfolio.status,
                    icon: FolderKanban,
                    onClick: () => onSelectPortfolio?.(portfolio)
                });
            }
        });
        // Search projects
        projects.forEach(project => {
            if (project.name.toLowerCase().includes(query) || project.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'project',
                    id: project.id,
                    title: project.name,
                    description: project.description,
                    status: project.status,
                    icon: Briefcase,
                    onClick: () => onSelectProject?.(project)
                });
            }
        });
        // Search organizations
        organizations.forEach(org => {
            if (org.name.toLowerCase().includes(query) || org.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'organization',
                    id: org.id,
                    title: org.name,
                    description: org.description,
                    status: org.is_active ? 'active' : 'inactive',
                    icon: Building2,
                    onClick: () => { } // No handler for now
                });
            }
        });
        // Search teams
        teams.forEach(team => {
            if (team.name.toLowerCase().includes(query) || team.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'team',
                    id: team.id,
                    title: team.name,
                    description: team.description,
                    status: 'active',
                    icon: Users2,
                    onClick: () => { } // No handler for now
                });
            }
        });
        // Search workspaces
        workspaces.forEach(workspace => {
            if (workspace.name.toLowerCase().includes(query) || workspace.description?.toLowerCase().includes(query)) {
                results.push({
                    type: 'workspace',
                    id: workspace.id,
                    title: workspace.name,
                    description: workspace.description,
                    status: workspace.isArchived ? 'archived' : 'active',
                    icon: Globe,
                    onClick: () => { } // No handler for now
                });
            }
        });
        return results;
    }, [searchQuery, missions, goals, portfolios, projects, organizations, teams, workspaces, onSelectMission, onSelectGoal, onSelectPortfolio, onSelectProject]);
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'bg-green-600 text-white';
            case 'on-track':
                return 'bg-green-600 text-white';
            case 'planning':
                return 'bg-yellow-600 text-white';
            case 'completed':
                return 'bg-blue-600 text-white';
            case 'on-hold':
                return 'bg-gray-600 text-white';
            case 'at-risk':
                return 'bg-orange-600 text-white';
            case 'behind':
                return 'bg-red-600 text-white';
            case 'inactive':
                return 'bg-gray-600 text-white';
            default:
                return 'bg-gray-600 text-white';
        }
    };
    return (_jsxs("div", { className: "max-w-7xl mx-auto", children: [_jsx("div", { className: "mb-8", children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5", style: { color: 'var(--text-secondary)' } }), _jsx("input", { type: "text", placeholder: "Search missions, goals, projects, organizations, teams, and more...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-12 pr-4 py-3 rounded-lg border focus:outline-none transition-all", style: {
                                backgroundColor: 'var(--bg-elevated)',
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontSize: '1rem'
                            } })] }) }), _jsx("div", { children: !searchQuery ? (_jsxs("div", { className: "text-center py-16 rounded-xl", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsx(Search, { className: "w-16 h-16 mx-auto mb-4", style: { color: 'var(--text-tertiary)' } }), _jsx("h3", { className: "text-xl font-semibold mb-2", style: { color: 'var(--text-secondary)' }, children: "Start searching" }), _jsx("p", { style: { color: 'var(--text-tertiary)' }, children: "Enter a keyword to search across all missions, goals, projects, and more" })] })) : allResults.length === 0 ? (_jsxs("div", { className: "text-center py-16 rounded-xl", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsx(Search, { className: "w-16 h-16 mx-auto mb-4", style: { color: 'var(--text-tertiary)' } }), _jsx("h3", { className: "text-xl font-semibold mb-2", style: { color: 'var(--text-secondary)' }, children: "No results found" }), _jsx("p", { style: { color: 'var(--text-tertiary)' }, children: "Try searching with different keywords" })] })) : (_jsx("div", { className: "space-y-4", children: ['mission', 'goal', 'portfolio', 'project', 'organization', 'team', 'workspace'].map(type => {
                        const typeResults = allResults.filter(r => r.type === type);
                        if (typeResults.length === 0)
                            return null;
                        const typeLabel = {
                            mission: 'Missions',
                            goal: 'Goals',
                            portfolio: 'Portfolios',
                            project: 'Projects',
                            organization: 'Organizations',
                            team: 'Teams',
                            workspace: 'Workspaces'
                        }[type];
                        return (_jsxs("div", { children: [_jsxs("h3", { className: "text-sm font-semibold mb-3 px-2", style: { color: 'var(--text-secondary)' }, children: [typeLabel, " (", typeResults.length, ")"] }), _jsx("div", { className: "space-y-2 mb-6", children: typeResults.map(result => {
                                        const IconComponent = result.icon;
                                        return (_jsx("button", { onClick: result.onClick, className: "w-full text-left p-4 rounded-lg border transition-all hover:shadow-md", style: {
                                                backgroundColor: 'var(--bg-elevated)',
                                                borderColor: 'var(--border-color)'
                                            }, children: _jsxs("div", { className: "flex items-start gap-4", children: [_jsx("div", { className: "flex-shrink-0 mt-1", style: { color: '#3B82F6' }, children: _jsx(IconComponent, { className: "w-5 h-5" }) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center justify-between gap-2 mb-1", children: [_jsx("h4", { className: "font-semibold truncate", style: { color: 'var(--text-primary)' }, children: result.title }), _jsx("span", { className: `px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${getStatusColor(result.status)}`, children: result.status })] }), result.description && (_jsx("p", { className: "text-sm line-clamp-2", style: { color: 'var(--text-secondary)' }, children: result.description }))] })] }) }, `${result.type}-${result.id}`));
                                    }) })] }, type));
                    }) })) })] }));
}
